/**
 * The key a plugin's credentials are locked to, made in the browser.
 *
 * The same pair `paradigm plugin keygen` makes (cli app_cmds._generate_app_keypair):
 *
 *   - public:  P-256, uncompressed point (65 bytes, 0x04…), base64url without
 *              padding: what the backend checks for `enc_public_key`
 *              (registration._location_error).
 *   - private: PKCS8 DER, base64url without padding: what the SDK's
 *              `decrypt_field` loads, set as PARADIGM_PLUGIN_PRIVATE_KEY.
 *
 * The private half never leaves this browser except as the file it downloads.
 */

export function b64url(bytes) {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = '';
  for (let i = 0; i < arr.length; i += 1) s += String.fromCharCode(arr[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromB64url(text) {
  const s = String(text || '').trim().replace(/-/g, '+').replace(/_/g, '/');
  const padded = s + '='.repeat((4 - (s.length % 4)) % 4);
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

/** True when `key` is what the backend accepts as `enc_public_key`. */
export function isPublicKey(key) {
  if (typeof key !== 'string' || !key.trim()) return false;
  if (!/^[A-Za-z0-9_-]+=*$/.test(key.trim())) return false;
  try {
    const raw = fromB64url(key);
    return raw.length === 65 && raw[0] === 4;
  } catch {
    return false;
  }
}

/** `{publicKey, privateKey}`, both base64url, as keygen writes them. */
export async function makeKeyPair(subtle = globalThis.crypto?.subtle) {
  if (!subtle) throw new Error('This browser cannot make keys.');
  const pair = await subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']);
  const pub = await subtle.exportKey('raw', pair.publicKey);
  const priv = await subtle.exportKey('pkcs8', pair.privateKey);
  return { publicKey: b64url(pub), privateKey: b64url(priv) };
}

/** The file the private half is saved as: an env line, ready to paste. */
export function privateKeyFile(pluginName, privateKey) {
  const slug = String(pluginName || 'plugin').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'plugin';
  return {
    filename: `${slug}.private-key.env`,
    text: `# The private key for ${pluginName || 'this plugin'}. Keep it where your handler runs.\n`
      + `PARADIGM_PLUGIN_PRIVATE_KEY=${privateKey}\n`,
  };
}

/** Save `text` as a file, once. */
export function download(filename, text) {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
